import React from "react";

const HorizontalBarCharts = ({
  data = [0, 0],
  categories = ["Total recyclable waste", "Total waste sent for recycling"],
  colors = ["#06513D", "#EAA89D"],
}) => {
  const val1 = data[0] || 0;
  const val2 = data[1] || 0;

  const maxValue = Math.max(val1, val2, 1);
  const pct1 = (val1 / maxValue) * 100;
  const pct2 = (val2 / maxValue) * 100;

  // Visual percentages with minimum 3% width for visibility on 0, 0 values
  const displayPct1 = pct1 === 0 ? 3 : pct1;
  const displayPct2 = pct2 === 0 ? 3 : pct2;

  const color1 = colors[0] || "#06513D";
  const color2 = colors[1] || "#EAA89D";

  const formatTick = (val) => {
    if (val === 0) return "0";
    return Number.isInteger(val) ? `${val}` : `${parseFloat(val.toFixed(1))}`;
  };

  const ticks = [
    "0",
    formatTick(maxValue * 0.2),
    formatTick(maxValue * 0.4),
    formatTick(maxValue * 0.6),
    formatTick(maxValue * 0.8),
    formatTick(maxValue),
  ];

  return (
    <div className="mt-6 w-full max-w-2xl mx-auto">
      {/* Chart container */}
      <div className="relative pl-[120px] sm:pl-[150px] pt-10 pb-10">
        {/* Y-axis Line (ends exactly at bottom-[35px]) */}
        <div className="absolute left-[120px] sm:left-[150px] top-6 bottom-[35px] w-px bg-gray-600">
          {/* Arrow pointing UP at the top */}
          <div className="absolute top-0 left-[-3px] w-0 h-0 border-l-[3px] border-r-[3px] border-b-[6px] border-transparent border-b-gray-600"></div>
        </div>

        {/* X-axis Line (placed exactly at bottom-[35px]) */}
        <div className="absolute left-[120px] sm:left-[150px] right-0 bottom-[35px] pr-[70px] sm:pr-[90px]">
          <div className="h-px bg-gray-600 w-full relative">
            {/* Arrow pointing right at the end of the line */}
            <div className="absolute right-[-6px] top-[-3px] w-0 h-0 border-t-[3px] border-b-[3px] border-l-[6px] border-transparent border-l-gray-600"></div>
          </div>
        </div>

        {/* Ticks Labels (placed below the X-axis line) */}
        <div className="absolute left-[120px] sm:left-[150px] right-0 bottom-0 pr-[70px] sm:pr-[90px]">
          <div className="relative text-[10px] sm:text-xs text-gray-600 h-6 w-full">
            {ticks.map((tick, idx) => {
              const pct = idx * 20;
              let transform = "translateX(-50%)";
              if (idx === 0) transform = "none";
              if (idx === 5) transform = "translateX(-100%)";
              return (
                <span
                  key={idx}
                  className="absolute"
                  style={{
                    left: `${pct}%`,
                    transform,
                    whiteSpace: "nowrap",
                  }}
                >
                  {tick}
                </span>
              );
            })}
          </div>
        </div>

        {/* Row 1 */}
        <div className="flex items-center min-h-[48px] relative mb-8 pr-[70px] sm:pr-[90px] w-full">
          {/* Label positioned absolutely to the left of the Y-axis */}
          <div className="absolute right-full mr-4 text-right text-xs sm:text-sm font-medium text-gray-700 w-[104px] sm:w-[134px] leading-tight">
            {categories[0] || "Total recyclable waste"}
          </div>
          {/* Bar */}
          <div className="flex-1 bg-gray-200 h-6 relative rounded-sm overflow-hidden">
            <div
              className="h-6 transition-all duration-1000 ease-out"
              style={{
                width: `${displayPct1}%`,
                backgroundColor: color1,
              }}
            ></div>
          </div>
          {/* Value text positioned absolutely inside the right padding area */}
          <span className="absolute right-0 w-[60px] sm:w-[80px] text-left text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap">
            {val1} Kg
          </span>
        </div>

        {/* Row 2 */}
        <div className="flex items-center min-h-[48px] relative mb-6 pr-[70px] sm:pr-[90px] w-full">
          {/* Label positioned absolutely to the left of the Y-axis */}
          <div className="absolute right-full mr-4 text-right text-xs sm:text-sm font-medium text-gray-700 w-[104px] sm:w-[134px] leading-tight">
            {categories[1] || "Total waste sent for recycling"}
          </div>
          {/* Bar */}
          <div className="flex-1 bg-gray-200 h-6 relative rounded-sm overflow-hidden">
            <div
              className="h-6 transition-all duration-1000 ease-out"
              style={{
                width: `${displayPct2}%`,
                backgroundColor: color2,
              }}
            ></div>
          </div>
          {/* Value text positioned absolutely inside the right padding area */}
          <span className="absolute right-0 w-[60px] sm:w-[80px] text-left text-xs sm:text-sm font-semibold text-gray-800 whitespace-nowrap">
            {val2} Kg
          </span>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-6 justify-center mt-6">
        <div className="flex items-center gap-2">
          <div
            className="w-5 h-5 rounded-sm"
            style={{ backgroundColor: color1 }}
          ></div>
          <span className="text-xs sm:text-sm text-gray-700">
            {categories[0] || "Total recyclable waste"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-5 h-5 rounded-sm"
            style={{ backgroundColor: color2 }}
          ></div>
          <span className="text-xs sm:text-sm text-gray-700">
            {categories[1] || "Total waste sent for recycling"}
          </span>
        </div>
      </div>
    </div>
  );
};

export default HorizontalBarCharts;
